import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SINGLE_QUOTES_REGEX } from '@common/constants';
import { BooleanOperator } from '@common/enums';
import { AutoActivationSettings, AzureGroup, NewGroupSettings } from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { SharedOdataService } from '@common/services';
import { UserGroupsTag, UserGroupsTagsMap } from '@common/services/smart-search/user-groups';
import { FilterOptions } from '@common/types';
import { containsWrapper, filterByWords, getFilterByContains, getFilterByEq } from '@common/utils/functions/search';
import { I18NextPipe } from 'angular-i18next';
import { I18_NAMESPACE_APPS_UI } from 'i18n';
import { SmartSearchModel, SmartSearchModelField } from 'mbs-ui-kit';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AzureGroupsService extends SharedOdataService<AzureGroup> {
  private readonly http = inject(HttpClient);
  private readonly i18nPipe = inject(I18NextPipe);

  readonly #autoActivation = UserGroupsTagsMap.get(UserGroupsTag.AutoActivation);
  readonly #name = UserGroupsTagsMap.get(UserGroupsTag.Name);

  protected readonly moduleUsers = I18_NAMESPACE_APPS_UI.users;

  get name(): { tagKey: string; prop: string } {
    return this.#name;
  }

  constructor() {
    super('AzureGroups');
  }

  fetchGroups(): Observable<ODataPagedResult<AzureGroup>> {
    const odataService = this.odataFactory.CreateService<AzureGroup>(`/AzureGroups`);

    return this.fetchData<AzureGroup>(odataService);
  }

  getNewGroupSettings(): Observable<NewGroupSettings> {
    const url = this.odataFactory.CreateService<string>(`/NewGroupSettings`).Query().GetUrl();

    return this.http.get<NewGroupSettings>(url);
  }

  updateNewGroupSettings(payload: NewGroupSettings): Observable<NewGroupSettings> {
    const url = this.odataFactory.CreateService<string>(`/NewGroupSettings`).Query().GetUrl();

    return this.http.post<NewGroupSettings>(url, payload);
  }

  updateAutoActivation(payload: AutoActivationSettings): Observable<AutoActivationSettings> {
    return this.odataFactory.CreateService<AzureGroup>(`/AzureGroups/AutoActivationMode`).Post(payload).Exec();
  }

  updateFilter(searchObj: SmartSearchModel): void {
    const filter: string[] = [];
    const nameTag = this.i18nPipe.transform(this.moduleUsers + this.#name.tagKey, { format: 'capitalize' });
    const autoActivationTag = this.i18nPipe.transform(this.moduleUsers + this.#autoActivation.tagKey, {
      format: 'capitalize'
    });

    if (searchObj[nameTag]) {
      const options: FilterOptions = {
        model: searchObj[nameTag] as SmartSearchModelField[],
        prop: this.#name.prop
      };

      filter.push(getFilterByContains(options));
    }

    if (searchObj[autoActivationTag]) {
      const options: FilterOptions = {
        model: searchObj[autoActivationTag] as SmartSearchModelField[],
        prop: this.#autoActivation.prop
      };

      filter.push(getFilterByEq(options, true));
    }

    if (searchObj.words?.filter(Boolean)) {
      const term = filterByWords(searchObj).replace(SINGLE_QUOTES_REGEX, '');

      filter.push(containsWrapper(this.#name.prop, term));
    }

    this.filter = filter.length > 0 ? filter.join(` ${BooleanOperator.And} `) : '';
  }
}
